import { useMemo } from 'react';
import { alpha, useTheme } from '@mui/material';
import Select, { GroupBase, Props, StylesConfig } from 'react-select';

export function useMuiSelectStyles<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(): StylesConfig<Option, IsMulti, Group> {
  const theme = useTheme();
  return useMemo(
    () => ({
      control: (base, state) => {
        const disabledBg =
          theme.palette.mode === 'dark' ? '#2A2F38' : '#F1F2F7';
        const borderColor = state.isDisabled
          ? theme.palette.action.disabled
          : state.isFocused
            ? theme.palette.primary.main
            : theme.palette.divider;
        return {
          ...base,
          border: `1px solid ${borderColor}`,
          boxShadow:
            state.isFocused && !state.isDisabled
              ? `0 0 0 1px ${theme.palette.primary.main}`
              : 'none',
          borderRadius: '12px',
          minHeight: '44px',
          backgroundColor: state.isDisabled
            ? disabledBg
            : theme.palette.background.paper,
          cursor: state.isDisabled ? 'not-allowed' : 'default',
          ':hover': {
            border: `1px solid ${
              state.isDisabled
                ? theme.palette.action.disabled
                : state.isFocused
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary
            }`,
          },
        };
      },
      menu: (base) => ({
        ...base,
        backgroundColor: theme.palette.background.paper,
        ...(theme.palette.mode === 'dark'
          ? {
              border: `1px solid ${theme.palette.divider}`,
            }
          : {}),
      }),
      // Above the modal layer: a portalled menu is a sibling of the dialog, so
      // anything lower renders behind a select opened inside one. Kept under
      // snackbar/tooltip so those still win.
      menuPortal: (base) => ({ ...base, zIndex: theme.zIndex.modal + 1 }),
      indicatorSeparator: (base) => ({ ...base, display: 'none' }),
      dropdownIndicator: (base, state) => ({
        ...base,
        color: state.isDisabled
          ? theme.palette.action.disabled
          : theme.palette.text.secondary,
      }),
      clearIndicator: (base) => ({
        ...base,
        color: theme.palette.text.secondary,
      }),
      placeholder: (base, state) => ({
        ...base,
        color: state.isDisabled
          ? theme.palette.text.disabled
          : alpha(theme.palette.text.primary, 0.25),
        fontWeight: 400,
      }),
      input: (base, state) => ({
        ...base,
        color: theme.palette.text.primary,
        cursor: state.isDisabled ? 'not-allowed' : 'auto',
        // iOS Safari auto-zooms the viewport when a focused field's font is
        // below 16px. The focusable element here is react-select's hidden
        // <input>, so keep it at 16px to prevent the zoom/distortion on mobile.
        // The visible value/placeholder size is unaffected (separate styles).
        fontSize: '16px',
      }),
      singleValue: (base, state) => ({
        ...base,
        color: state.isDisabled
          ? theme.palette.text.disabled
          : theme.palette.text.primary,
        fontWeight: 400,
      }),
      multiValue: (base) => ({
        ...base,
        backgroundColor: 'transparent',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '12px',
        padding: '1px 2px 1px 8px',
        gap: '2px',
        alignItems: 'center',
      }),
      multiValueLabel: (base, state) => ({
        ...base,
        fontSize: '14px',
        color: state.isDisabled
          ? theme.palette.text.disabled
          : theme.palette.text.primary,
      }),
      multiValueRemove: (base) => ({
        ...base,
        cursor: 'pointer',
        borderRadius: '50%',
        height: '20px',
        width: '20px',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.text.secondary,
        ':hover': {
          backgroundColor: alpha(theme.palette.text.secondary, 0.1),
          color: theme.palette.text.primary,
        },
        '& > svg': {
          height: '14px',
          width: '14px',
        },
      }),
      option: (base, state) => ({
        ...base,
        borderRadius: '12px',
        backgroundColor: state.isSelected
          ? alpha(theme.palette.primary.main, 0.12)
          : state.isFocused
            ? alpha(theme.palette.primary.main, 0.08)
            : 'transparent',
        color: theme.palette.text.primary,
        ':active': {
          backgroundColor: alpha(theme.palette.primary.main, 0.2),
        },
      }),

      menuList: (base) => ({
        ...base,
        padding: '12px',
        '@media screen and (max-width: 900px)': {
          maxHeight: '200px',
        },
      }),
      noOptionsMessage: (base) => ({
        ...base,
        color: theme.palette.text.secondary,
      }),
      loadingMessage: (base) => ({
        ...base,
        color: theme.palette.text.secondary,
      }),
    }),
    [theme],
  );
}

function MuiReactSelect<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({ styles: customStyles, ...props }: Props<Option, IsMulti, Group>) {
  const theme = useTheme();
  const baseStyles = useMuiSelectStyles<Option, IsMulti, Group>();

  const mergedStyles = useMemo<StylesConfig<Option, IsMulti, Group>>(() => {
    if (!customStyles) return baseStyles;
    const result = { ...baseStyles };
    (
      Object.keys(customStyles) as Array<
        keyof StylesConfig<Option, IsMulti, Group>
      >
    ).forEach((key) => {
      const baseFn = baseStyles[key] as ((b: any, s: any) => any) | undefined;
      const customFn = customStyles[key] as
        | ((b: any, s: any) => any)
        | undefined;
      if (baseFn && customFn) {
        // Chain: react-select base → MUI styles → custom styles
        result[key] = (b: any, s: any) => customFn(baseFn(b, s), s) as any;
      } else if (customFn) {
        result[key] = customFn as any;
      }
    });
    return result;
  }, [baseStyles, customStyles]);

  return (
    <Select
      theme={(t) => ({
        ...t,
        borderRadius: 12,
        colors: {
          ...t.colors,
          primary: theme.palette.primary.main,
          neutral5: theme.palette.background.default,
          neutral80: theme.palette.text.primary,
        },
      })}
      menuPosition="fixed"
      // Portalled so the menu escapes the overflow of whatever scrolls around
      // it; `menuPosition: fixed` alone is still clipped by any ancestor that
      // establishes a containing block (a transform or filter is enough).
      menuPortalTarget={document.body}
      {...props}
      styles={mergedStyles}
    />
  );
}

export default MuiReactSelect;
